/**
 * VFS — ドロップされたものを「パス → Blob を返す遅延エントリ」の集合に正規化する。
 *
 * 素のフォルダ / ファイル : File をそのまま保持（ディスクから遅延読み込み、メモリ消費ゼロ）
 * .zip                    : central directory だけ読んでインデックス化。stored エントリはスライスのみ（ゼロコピー）
 * .tar                    : ヘッダだけ拾ってインデックス化（全エントリがゼロコピー）
 * .tar.gz / .tgz / .gz    : gzip はシークできないため、ストリーム展開して OPFS に一時展開する
 */

const KB = 1024, MB = KB * KB;

export class Entry {
  constructor(path, size, load){ this.path = path; this.size = size; this._load = load; }
  blob(){ return this._blob ??= Promise.resolve(this._load()); }
  async arrayBuffer(){ return (await this.blob()).arrayBuffer(); }
  async text(){ return (await this.blob()).text(); }
  async json(){ return JSON.parse(await this.text()); }
  /** hyparquet 用の AsyncBuffer（列単位のレンジ読みだけを行う） */
  async asyncBuffer(){
    const b = await this.blob();
    return { byteLength: b.size, slice: (s, e) => b.slice(s, e ?? b.size).arrayBuffer() };
  }
  url(){ return this._url ??= this.blob().then(b => URL.createObjectURL(b)); }
  async revoke(){ if(this._url) URL.revokeObjectURL(await this._url); this._url = null; }
}

export class Vfs {
  constructor(){ this.map = new Map(); this.notes = []; }
  add(path, size, load){
    path = path.replace(/^\.?\//, '');
    if(!path || path.endsWith('/')) return;
    const base = path.split('/').pop();
    if(base.startsWith('._') || base === '.DS_Store') return;   // macOS のゴミ
    if(path.includes('__MACOSX/')) return;
    this.map.set(path, new Entry(path, size, load));
  }
  get(p){ return this.map.get(p); }
  has(p){ return this.map.has(p); }
  paths(){ return [...this.map.keys()]; }
  /** 正規表現に合うパスを昇順で返す */
  glob(re){ return this.paths().filter(p => re.test(p)).sort(); }
  get totalSize(){ let n = 0; for(const e of this.map.values()) n += e.size; return n; }
  /** meta/info.json の位置を基準に共通のルートプレフィックスを剥がす */
  rebase(){
    const anchor = this.paths().find(p => /(^|\/)meta\/info\.json$/.test(p));
    if(!anchor) return this;
    const root = anchor.slice(0, anchor.length - 'meta/info.json'.length);
    if(!root) return this;
    const next = new Vfs(); next.notes = this.notes;
    for(const [p, e] of this.map)
      if(p.startsWith(root)) next.map.set(p.slice(root.length), new Entry(p.slice(root.length), e.size, e._load));
    return next;
  }
  async revokeAll(){ for(const e of this.map.values()) await e.revoke(); }
}

/* ---------------- ドロップ入力の走査 ---------------- */

const ARCHIVE = /\.(zip|tar|tar\.gz|tgz|gz)$/i;

/** DataTransfer から File[]（フォルダは再帰展開）を得る */
export async function filesFromDataTransfer(dt){
  const items = [...dt.items].filter(i => i.kind === 'file');
  const entries = items.map(i => i.webkitGetAsEntry?.()).filter(Boolean);
  if(!entries.length) return [...dt.files].map(f => ({ file: f, path: f.name }));
  const out = [];
  await Promise.all(entries.map(e => walkEntry(e, '', out)));
  return out;
}

function walkEntry(entry, prefix, out){
  return new Promise(resolve => {
    const path = prefix ? `${prefix}/${entry.name}` : entry.name;
    if(entry.isFile){
      entry.file(f => { out.push({ file: f, path }); resolve(); }, () => resolve());
    } else if(entry.isDirectory){
      const rd = entry.createReader(); const all = [];
      const readMore = () => rd.readEntries(async batch => {
        if(!batch.length){
          await Promise.all(all.map(c => walkEntry(c, path, out)));
          return resolve();
        }
        all.push(...batch); readMore();
      }, () => resolve());
      readMore();
    } else resolve();
  });
}

/**
 * 入力（{file, path}[]）を VFS にする。アーカイブが1つだけなら展開する。
 * onProgress({phase, detail, done, total})
 */
export async function buildVfs(inputs, onProgress = () => {}){
  const vfs = new Vfs();
  const archives = inputs.filter(i => ARCHIVE.test(i.file.name));
  const plain = inputs.filter(i => !ARCHIVE.test(i.file.name));

  for(const { file, path } of plain) vfs.add(path, file.size, () => file);

  for(const { file } of archives){
    const n = file.name.toLowerCase();
    onProgress({ phase: 'archive', detail: file.name });
    if(n.endsWith('.zip'))                      await readZip(file, vfs, onProgress);
    else if(n.endsWith('.tar'))                 await readTar(file, vfs, onProgress);
    else if(n.endsWith('.tar.gz') || n.endsWith('.tgz')) await readTarGz(file, vfs, onProgress);
    else if(n.endsWith('.gz'))                  await readGz(file, vfs, onProgress);
  }
  return vfs.rebase();
}

/* ---------------- zip ---------------- */

const u16 = (v, o) => v.getUint16(o, true);
const u32 = (v, o) => v.getUint32(o, true);
const u64 = (v, o) => Number(v.getBigUint64(o, true));

async function dv(file, start, end){
  return new DataView(await file.slice(start, Math.min(end, file.size)).arrayBuffer());
}

export async function readZip(file, vfs, onProgress = () => {}){
  const tailLen = Math.min(file.size, 64 * KB + 22);
  const tail = await dv(file, file.size - tailLen, file.size);
  let eocd = -1;
  for(let i = tail.byteLength - 22; i >= 0; i--) if(u32(tail, i) === 0x06054b50){ eocd = i; break; }
  if(eocd < 0) throw new Error('zip: End of Central Directory が見つかりません');

  let count = u16(tail, eocd + 10), cdSize = u32(tail, eocd + 12), cdOff = u32(tail, eocd + 16);
  if(cdOff === 0xffffffff || count === 0xffff){                       // ZIP64
    let loc = -1;
    for(let i = eocd - 20; i >= 0; i--) if(u32(tail, i) === 0x07064b50){ loc = i; break; }
    if(loc < 0) throw new Error('zip64: locator が見つかりません');
    const rec = await dv(file, u64(tail, loc + 8), u64(tail, loc + 8) + 56);
    if(u32(rec, 0) !== 0x06064b50) throw new Error('zip64: EOCD レコードが不正です');
    count = u64(rec, 32); cdSize = u64(rec, 40); cdOff = u64(rec, 48);
  }

  const cd = await dv(file, cdOff, cdOff + cdSize);
  const dec = new TextDecoder();
  let p = 0;
  for(let k = 0; k < count && p + 46 <= cd.byteLength; k++){
    if(u32(cd, p) !== 0x02014b50) break;
    const method = u16(cd, p + 10);
    let comp = u32(cd, p + 20), size = u32(cd, p + 24);
    const nameLen = u16(cd, p + 28), extraLen = u16(cd, p + 30), cmtLen = u16(cd, p + 32);
    let local = u32(cd, p + 42);
    const name = dec.decode(new Uint8Array(cd.buffer, cd.byteOffset + p + 46, nameLen));

    let x = p + 46 + nameLen;                                          // zip64 extra field
    const xEnd = x + extraLen;
    while(x + 4 <= xEnd){
      const id = u16(cd, x), len = u16(cd, x + 2); let q = x + 4;
      if(id === 0x0001){
        if(size === 0xffffffff){ size = u64(cd, q); q += 8; }
        if(comp === 0xffffffff){ comp = u64(cd, q); q += 8; }
        if(local === 0xffffffff){ local = u64(cd, q); q += 8; }
      }
      x += 4 + len;
    }
    p = xEnd + cmtLen;

    if(name.endsWith('/')){ continue; }
    vfs.add(name, size, async () => {
      const lh = await dv(file, local, local + 30);
      const off = local + 30 + u16(lh, 26) + u16(lh, 28);
      const raw = file.slice(off, off + comp);
      if(method === 0) return raw;                                     // stored → ゼロコピー
      if(method === 8) return inflateRaw(raw);
      throw new Error(`zip: 未対応の圧縮方式 ${method} (${name})`);
    });
  }
  vfs.notes.push(`zip: ${count} エントリを索引化`);
  onProgress({ phase: 'archive', detail: `${file.name}: ${count} エントリ` });
}

async function inflateRaw(blob){
  if(typeof DecompressionStream === 'undefined') throw new Error('DecompressionStream 非対応のブラウザです');
  return new Response(blob.stream().pipeThrough(new DecompressionStream('deflate-raw'))).blob();
}

/* ---------------- tar ---------------- */

const octal = (bytes) => {
  if(bytes[0] & 0x80){                                  // GNU base-256
    let n = bytes[0] & 0x7f;
    for(let i = 1; i < bytes.length; i++) n = n * 256 + bytes[i];
    return n;
  }
  const s = new TextDecoder().decode(bytes).replace(/\0.*$/, '').trim();
  return s ? parseInt(s, 8) : 0;
};

/** 512B ヘッダを解析。null = 終端ブロック */
function tarHeader(buf){
  const b = new Uint8Array(buf);
  if(b.every(v => v === 0)) return null;
  const str = (o, n) => new TextDecoder().decode(b.subarray(o, o + n)).replace(/\0.*$/, '');
  const name = str(0, 100), prefix = str(345, 155);
  return {
    name: prefix ? `${prefix}/${name}` : name,
    size: octal(b.subarray(124, 136)),
    type: String.fromCharCode(b[156] || 0x30),
  };
}

/** pax 拡張ヘッダ（"len key=value\n" の連結）から path を取り出す */
function paxPath(text){
  let out = null;
  for(const m of text.matchAll(/(\d+) ([^=]+)=([^\n]*)\n/g)) if(m[2] === 'path') out = m[3];
  return out;
}

export async function readTar(file, vfs, onProgress = () => {}){
  let off = 0, n = 0, override = null;
  while(off + 512 <= file.size){
    const h = tarHeader(await file.slice(off, off + 512).arrayBuffer());
    off += 512;
    if(!h){                                            // 終端は 512B ゼロブロック×2
      if(off + 512 > file.size) break;
      const h2 = tarHeader(await file.slice(off, off + 512).arrayBuffer());
      if(!h2) break;
      continue;
    }
    const dataOff = off, padded = Math.ceil(h.size / 512) * 512;
    off += padded;

    if(h.type === 'x' || h.type === 'X'){               // pax: 次エントリのパス
      override = paxPath(await file.slice(dataOff, dataOff + h.size).text());
      continue;
    }
    if(h.type === 'L'){                                 // GNU longname
      override = (await file.slice(dataOff, dataOff + h.size).text()).replace(/\0.*$/, '');
      continue;
    }
    if(h.type === 'g' || h.type === 'K'){ continue; }
    const path = override || h.name; override = null;
    if(h.type === '5' || path.endsWith('/')) continue;
    if(h.type !== '0' && h.type !== '\0' && h.type !== '7') continue;

    vfs.add(path, h.size, () => file.slice(dataOff, dataOff + h.size));   // ゼロコピー
    n++;
    if(n % 200 === 0) onProgress({ phase: 'archive', detail: `${file.name}: ${n} エントリ` });
  }
  vfs.notes.push(`tar: ${n} エントリを索引化`);
}

/* ---------------- tar.gz / gz（OPFS へ展開） ---------------- */

async function opfsDir(){
  if(!navigator.storage?.getDirectory) return null;
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle('lerobot-staging', { create: true });
}

export async function clearStaging(){
  if(!navigator.storage?.getDirectory) return;
  const root = await navigator.storage.getDirectory();
  await root.removeEntry('lerobot-staging', { recursive: true }).catch(() => {});
}

/** gzip ストリームを tar として解釈し、各エントリを OPFS に書き出す */
export async function readTarGz(file, vfs, onProgress = () => {}){
  const dir = await opfsDir();
  const canWrite = dir && typeof FileSystemFileHandle !== 'undefined'
                       && 'createWritable' in FileSystemFileHandle.prototype;
  if(!canWrite) vfs.notes.push('OPFS 非対応のためメモリ上に展開します（大きな .tar.gz は不足する可能性あり）');

  const est = await navigator.storage?.estimate?.().catch(() => null);
  if(est?.quota) vfs.notes.push(`ストレージ空き見込み: ${(est.quota / 1e9).toFixed(1)} GB`);

  const reader = file.stream().pipeThrough(new DecompressionStream('gzip')).getReader();
  const st = new TarStream();
  let read = 0, n = 0;

  const sink = async (path, size, chunks) => {
    if(canWrite){
      const key = encodeURIComponent(path);
      const fh = await dir.getFileHandle(key, { create: true });
      const w = await fh.createWritable();
      for(const c of chunks) await w.write(c);
      await w.close();
      vfs.add(path, size, async () => (await (await dir.getFileHandle(key)).getFile()));
    } else {
      const blob = new Blob(chunks);
      vfs.add(path, size, () => blob);
    }
    n++;
    onProgress({ phase: 'extract', detail: path, done: read, total: null, count: n });
  };

  for(;;){
    const { value, done } = await reader.read();
    if(done) break;
    read += value.byteLength;
    await st.push(value, sink);
    if(n % 5 === 0) onProgress({ phase: 'extract', detail: `展開中… ${(read / MB).toFixed(0)} MB`, done: read, count: n });
  }
  await st.flush(sink);
  vfs.notes.push(`tar.gz: ${n} エントリを展開（${(read / MB).toFixed(0)} MB）`);
}

export async function readGz(file, vfs, onProgress = () => {}){
  if(/\.tar\.gz$|\.tgz$/i.test(file.name)) return readTarGz(file, vfs, onProgress);
  const out = await new Response(file.stream().pipeThrough(new DecompressionStream('gzip'))).blob();
  vfs.add(file.name.replace(/\.gz$/i, ''), out.size, () => out);
}

/** チャンク境界をまたぐ tar のストリーム解析 */
export class TarStream {
  constructor(){ this.buf = new Uint8Array(0); this.cur = null; this.override = null; }
  #concat(a, b){ const o = new Uint8Array(a.length + b.length); o.set(a); o.set(b, a.length); return o; }

  async push(chunk, sink){
    this.buf = this.#concat(this.buf, chunk);
    for(;;){
      if(this.cur){                                        // 本体を読んでいる途中
        if(this.cur.remain > 0){
          if(this.buf.length === 0) return;
          const take = Math.min(this.cur.remain, this.buf.length);
          this.cur.chunks.push(this.buf.slice(0, take));
          this.cur.remain -= take;
          this.buf = this.buf.subarray(take);
          if(this.cur.remain > 0) return;
          this.cur.pad = (512 - (this.cur.size % 512)) % 512;
        }
        // 本体の後ろのパディングを捨てる。ここはチャンク境界をまたぐことがあるので、
        // 残り byte 数を持ち越す（次の push でまた pad ぶん読み飛ばすと 512 の目盛りがずれ、
        // 以降のヘッダが読めなくなる）
        if(this.cur.pad > 0){
          const drop = Math.min(this.cur.pad, this.buf.length);
          this.cur.pad -= drop;
          this.buf = this.buf.subarray(drop);
          if(this.cur.pad > 0) return;
        }
        await this.#emit(sink);
        continue;
      }
      if(this.buf.length < 512) return;
      const h = tarHeader(this.buf.buffer.slice(this.buf.byteOffset, this.buf.byteOffset + 512));
      this.buf = this.buf.subarray(512);
      if(!h) continue;
      this.cur = { ...h, remain: h.size, pad: 0, chunks: [] };
    }
  }

  async #emit(sink){
    const c = this.cur; this.cur = null;
    const body = () => new Blob(c.chunks);
    if(c.type === 'x' || c.type === 'X'){ this.override = paxPath(await body().text()); return; }
    if(c.type === 'L'){ this.override = (await body().text()).replace(/\0.*$/, ''); return; }
    if(c.type === 'g' || c.type === 'K') return;
    const path = this.override || c.name; this.override = null;
    if(c.type === '5' || path.endsWith('/')) return;
    if(c.type !== '0' && c.type !== '\0' && c.type !== '7') return;
    await sink(path, c.size, c.chunks);
  }

  async flush(sink){ if(this.cur && this.cur.remain === 0) await this.#emit(sink); }
}
